import React, { useMemo } from 'react';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Button,
  CardContent,
  FilePdfIcon,
  Grid,
  Link,
  List,
  ListItem,
  Paper,
  Typography,
} from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { AvailityHeaderLogo, Footer } from '@/components';
import { getHiddenValueCallbackById } from '@/global';
import { useFRCallback } from '@/hooks';
import { TwoFAIcon03 } from '@/static';

const specificEnv = getSpecificEnv();

const faqLink = 'https://www.availity.com/documents/learning/QRG_AP_2step_Providers.pdf';

const TwoFAHardTokenEnrollment = ({ ...props }) => {
  const { stepToRender, setCurrentStep } = props;
  const {
    callbacks = [],
    confirmationCallbackIndex,
    confirmationCallbackOptions,
    textOutputCallback,
  } = useFRCallback(stepToRender);

  // get qrcode values
  const qrCodeFullSecret = find(
    textOutputCallback?.output,
    (outputObj) => outputObj?.name?.toLowerCase() === 'message'
  )?.value;

  // insert env into qrcode string if in nonprod
  let qrCodeToUse = qrCodeFullSecret;
  if (specificEnv !== 'prod') {
    const insertionIndex = qrCodeFullSecret.indexOf('Availity');
    const prefix = qrCodeFullSecret.slice(0, insertionIndex);
    const suffix = qrCodeFullSecret.slice(insertionIndex);
    qrCodeToUse = `${prefix}${specificEnv?.toUpperCase()}-${suffix}`;
  }

  const totpSecretKey = useMemo(() => {
    let result = '';
    if (callbacks) {
      const secretKeyCallback = getHiddenValueCallbackById(callbacks, 'totpsecret');
      result =
        secretKeyCallback &&
        find(secretKeyCallback.output, (outputObj) => outputObj?.name?.toLowerCase() === 'value')?.value;
    }

    return result;
  }, [callbacks]);

  const handleNavigation = (selected) => {
    const nextStep = { ...stepToRender };
    const newCallbacks = [...callbacks];

    // set the confirmation callback input based off the index of the selected button navigation in the callback
    if (selected === 'next') {
      const nextButtonIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'next');

      newCallbacks[confirmationCallbackIndex].input[0] = {
        ...newCallbacks[confirmationCallbackIndex].input[0],
        value: nextButtonIndex,
      };
    } else if (selected === 'back') {
      const backButtonIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'back');

      newCallbacks[confirmationCallbackIndex].input[0] = {
        ...newCallbacks[confirmationCallbackIndex].input[0],
        value: backButtonIndex,
      };
    }

    // set the newCallbacks in the newStep
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger navigation
    setCurrentStep({ ...nextStep });
  };

  return (
    <Grid
      id="2fa-hard-token-enrollment-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-start-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <CardContent>
              <Grid display="flex" flexDirection="row" flexWrap="wrap">
                <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                  <Box textAlign="center" mb={2}>
                    <img
                      src={TwoFAIcon03}
                      alt="2FA Icon"
                      style={{
                        maxWidth: '45%',
                      }}
                    />
                  </Box>
                  <Typography variant="subtitle1" mb={2}>
                    Organizations that do not allow employees to access their cellphone may utilize a hard token device
                    as their 2-step authentication method.
                  </Typography>
                  <Typography variant="subtitle1" mb={2}>
                    This method may require assistance from your organization&apos;s IT administrator. The hard token
                    devices are available from online retailers.
                  </Typography>
                  <Box mt={3}>
                    <Link
                      href={faqLink}
                      target="_blank"
                      loadApp={false}
                      rel="noopener noreferrer"
                      aria-label="Get answers to common questions (opens PDF in a new window)"
                    >
                      Answers to common questions <FilePdfIcon fontSize="small" />
                    </Link>
                  </Box>
                </Grid>
                <Grid
                  display="flex"
                  flexDirection="column"
                  size={{ md: 8 }}
                  sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                >
                  <Box pl={2}>
                    <Typography variant="h1" mb={1}>
                      2-Step Authentication
                    </Typography>
                    <Typography variant="h4" component="h2" mb={2}>
                      How to Set Up Hard Tokens
                    </Typography>
                  </Box>
                  <Grid display="flex" flexDirection="column" flexGrow={1} pl={2} pt={2}>
                    <List
                      component="ol"
                      sx={{
                        listStyleType: 'decimal',
                        listStylePosition: 'outside',
                        paddingX: 2,
                      }}
                    >
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Typography component="span" variant="subtitle1">
                          Follow the installation instructions that come with your hard token device (may require
                          assistance from your IT administrator)
                        </Typography>
                      </ListItem>
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Box>
                          <Typography variant="subtitle1" mb={3}>
                            Scan this QR code:
                          </Typography>
                          <Box mb={2} textAlign="center">
                            <QRCodeSVG value={qrCodeToUse} size={150} />
                          </Box>
                          <Typography mb={2} textAlign="center" sx={{ fontWeight: 'bold' }}>
                            OR
                          </Typography>
                          <Typography variant="subtitle1" mb={2}>
                            Enter this secret key into your time-based authentication device:
                          </Typography>
                          <Typography variant="subtitle1" textAlign="center">
                            <Box sx={{ fontWeight: 'bold' }}>{totpSecretKey}</Box>
                          </Typography>
                        </Box>
                      </ListItem>
                    </List>
                    <Box textAlign="right" mt="auto">
                      <Button color="secondary" onClick={() => handleNavigation('back')}>
                        Back
                      </Button>{' '}
                      <Button color="primary" onClick={() => handleNavigation('next')}>
                        Continue
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

TwoFAHardTokenEnrollment.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default TwoFAHardTokenEnrollment;
